"use client"

import type React from "react"

import { useState, useEffect, useRef, useMemo } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { MapPin, X } from "lucide-react"
import locationData from "../../../data/location.json"

type LocationSearchProps = {
  onLocationSelect: (location: string) => void
  selectedLocation: string
  onClear: () => void
}

export default function LocationSearch({ onLocationSelect, selectedLocation, onClear }: LocationSearchProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const allLocations = useMemo(
    () =>
      Object.entries(locationData).flatMap(([country, regions]) => {
        return regions.flatMap((region) => {
          const regionName = "state" in region ? region.state : region.province
          return region.cities.map((city) => `${city}, ${regionName}, ${country}`)
        })
      }),
    [],
  )

  const suggestions = useMemo(() => {
    if (searchTerm.length > 1) {
      return allLocations.filter((location) => location.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 10)
    }
    return []
  }, [searchTerm, allLocations])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSelectLocation = (location: string) => {
    onLocationSelect(location)
    setSearchTerm("")
    setShowSuggestions(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setShowSuggestions(value.length > 1)
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder={selectedLocation || "Search by city, state, or country..."}
          value={searchTerm}
          onChange={handleInputChange}
          className="pl-10 pr-10 w-full h-12"
          onFocus={() => setShowSuggestions(searchTerm.length > 1)}
        />
        {(searchTerm || selectedLocation) && (
          <Button
            variant="ghost"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            onClick={() => {
              setSearchTerm("")
              onClear()
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 mt-1 w-full bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((location, index) => (
            <div
              key={index}
              className="px-4 py-3 hover:bg-muted cursor-pointer flex items-center"
              onClick={() => handleSelectLocation(location)}
            >
              <MapPin className="mr-2 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="text-sm">{location}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

